import { Game } from '../../features/game/types/Game'
import { implementationGameListFields } from '../features/implementation-examples/helpers/implementationExampleHelpers'
import {
  ImplementationScreenshotCountsMap,
  InternalScreenshotComment,
  MultipleScreenshotCommentMutation,
  ScreenshotCommentMutation,
} from '../features/implementation-examples/types/ImplementationExamples'
import { customError, optionalJson } from './helpers'
import { internalApiserverApi } from './internal-apiserver'
import { InternalGameScreenshot } from './screenshot-links'

export const notStoredError = 'not stored'
export const stillStoredError = 'still stored'

// A missing comment answers 204 with an empty body.
const commentRequest = (id: string) => ({ url: `i/screenshotcomments/${id}`, responseHandler: optionalJson })
const screenshotsRequest = (ids: string[]) => ({ url: 'screenshots/list', method: 'POST', body: ids })

export type ImplementationGamesResult = { counts: ImplementationScreenshotCountsMap; games: Game[] }

export type SavedScreenshotComment = { comment: InternalScreenshotComment; screenshot?: InternalGameScreenshot }

export type SavedMultipleScreenshotComment = { screenshots: InternalGameScreenshot[]; comments: Record<string, InternalScreenshotComment | null> }

export type DeletedScreenshotComment = { screenshot?: InternalGameScreenshot }

const implementationExamplesApi = internalApiserverApi
  .enhanceEndpoints({ addTagTypes: ['internalScreenshotComment', 'internalImplementationScreenshots'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      // The backend picks the market's games: those with screenshots there, and the top 200 without.
      getInternalImplementationGames: builder.query<ImplementationGamesResult, string>({
        async queryFn(marketIso, _api, _extraOptions, baseQuery) {
          const counts = await baseQuery({ url: 'screenshots/map', params: { marketIso, includeGamesWithoutScreenshots: true } })
          if (counts.error) return { error: counts.error }
          const countsMap = (counts.data ?? {}) as ImplementationScreenshotCountsMap
          const gameIds = Object.keys(countsMap)
          if (!gameIds.length) return { data: { counts: countsMap, games: [] } }
          const games = await baseQuery({ url: 'games/list', method: 'POST', body: { gameIds, include: implementationGameListFields.join(',') } })
          if (games.error) return { error: games.error }
          return { data: { counts: countsMap, games: (games.data ?? []) as Game[] } }
        },
      }),
      getInternalScreenshotsByIds: builder.query<InternalGameScreenshot[], string[]>({
        query: screenshotsRequest,
        providesTags: (result, _error, ids) => [...ids, ...(result ?? []).map(({ id }) => id)].map((id) => ({ type: 'internalImplementationScreenshots', id })),
      }),
      getInternalScreenshotComment: builder.query<InternalScreenshotComment | null, string>({
        query: commentRequest,
        providesTags: (_result, _error, id) => [{ type: 'internalScreenshotComment', id }],
      }),
      // Creates with `id: null`. Returns the comment read again, and its screenshot read again.
      saveScreenshotComment: builder.mutation<SavedScreenshotComment, ScreenshotCommentMutation>({
        async queryFn(comment, _api, _extraOptions, baseQuery) {
          const saved = await baseQuery({ url: 'i/screenshotcomments', method: 'POST', body: comment, responseHandler: optionalJson })
          if (saved.error) return { error: saved.error }
          const screenshots = comment.screenshotId ? await baseQuery(screenshotsRequest([comment.screenshotId])) : undefined
          if (screenshots?.error) return { error: screenshots.error }
          const screenshot = ((screenshots?.data ?? []) as InternalGameScreenshot[]).find(({ id }) => id === comment.screenshotId)
          const id = (saved.data as { id?: string } | null)?.id ?? comment.id ?? screenshot?.commentId
          if (!id) return { error: customError(notStoredError) }
          const stored = await baseQuery(commentRequest(id))
          if (stored.error) return { error: stored.error }
          if (!stored.data) return { error: customError(notStoredError) }
          return { data: { comment: stored.data as InternalScreenshotComment, screenshot } }
        },
        invalidatesTags: (result, _error, { screenshotId }) =>
          result
            ? [
                { type: 'internalScreenshotComment', id: result.comment.id },
                { type: 'internalImplementationScreenshots', id: screenshotId ?? '' },
              ]
            : [],
      }),
      // Returns every screenshot read again, with the comment each now links to.
      saveMultipleScreenshotComment: builder.mutation<SavedMultipleScreenshotComment, MultipleScreenshotCommentMutation>({
        async queryFn(body, _api, _extraOptions, baseQuery) {
          const saved = await baseQuery({ url: 'i/screenshotcomments/multiple', method: 'POST', body, responseHandler: optionalJson })
          if (saved.error) return { error: saved.error }
          const read = await baseQuery(screenshotsRequest(body.screenshotIds))
          if (read.error) return { error: read.error }
          const screenshots = (read.data ?? []) as InternalGameScreenshot[]
          const comments: Record<string, InternalScreenshotComment | null> = {}
          for (const commentId of Array.from(new Set(screenshots.map(({ commentId }) => commentId).filter((id): id is string => !!id)))) {
            const stored = await baseQuery(commentRequest(commentId))
            if (stored.error) return { error: stored.error }
            comments[commentId] = (stored.data as InternalScreenshotComment | null) ?? null
          }
          return { data: { screenshots, comments } }
        },
        invalidatesTags: (result, _error, { screenshotIds }) =>
          result
            ? [
                ...screenshotIds.map((id) => ({ type: 'internalImplementationScreenshots' as const, id })),
                ...Object.keys(result.comments).map((id) => ({ type: 'internalScreenshotComment' as const, id })),
              ]
            : [],
      }),
      // Resolves only when a fresh read no longer finds the comment. Returns the screenshot read again.
      deleteScreenshotComment: builder.mutation<DeletedScreenshotComment, { id: string; screenshotId?: string | null }>({
        async queryFn({ id, screenshotId }, _api, _extraOptions, baseQuery) {
          const deleted = await baseQuery({ url: `i/screenshotcomments/${id}`, method: 'DELETE', responseHandler: 'text' })
          if (deleted.error) return { error: deleted.error }
          const stored = await baseQuery(commentRequest(id))
          if (stored.error) return { error: stored.error }
          if (stored.data) return { error: customError(stillStoredError) }
          if (!screenshotId) return { data: {} }
          const screenshots = await baseQuery(screenshotsRequest([screenshotId]))
          if (screenshots.error) return { error: screenshots.error }
          return { data: { screenshot: ((screenshots.data ?? []) as InternalGameScreenshot[]).find((screenshot) => screenshot.id === screenshotId) } }
        },
        invalidatesTags: (_result, error, { id, screenshotId }) =>
          error
            ? []
            : [
                { type: 'internalScreenshotComment', id },
                { type: 'internalImplementationScreenshots', id: screenshotId ?? '' },
              ],
      }),
    }),
  })

export const {
  useGetInternalImplementationGamesQuery,
  useGetInternalScreenshotsByIdsQuery,
  useGetInternalScreenshotCommentQuery,
  useSaveScreenshotCommentMutation,
  useSaveMultipleScreenshotCommentMutation,
  useDeleteScreenshotCommentMutation,
} = implementationExamplesApi
